import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Typography,
  Alert,
  Input,
  Checkbox,
  List,
  Tag,
  Spin,
  Empty,
  message,
  Divider,
  Progress,
  Row,
  Col,
  Tooltip,
  Badge,
  Modal,
  Timeline,
  Tour,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  EyeOutlined,
  RollbackOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { v2ArticlesApi, publicSamplesApi } from '../../../../api';
import * as Diff from 'diff';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import EnhancedTour from '../../../Common/EnhancedTour';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// デフォルトブラッシュアップ項目
const DEFAULT_REFINEMENT_ITEMS = [
  { id: 'style_consistency', label: 'スタイルとの整合性', checked: true },
  { id: 'article_consistency', label: '記事全体の一貫性', checked: true },
  { id: 'notation_check', label: '表記揺れチェック', checked: true },
  { id: 'readability', label: '読みやすさの改善', checked: true },
];

// プラン別ブラッシュアップ回数上限
const PLAN_LIMITS = {
  free: 0,
  light: 1,
  standard: 2,
  premium: 3,
  custom: 3,
  admin: 999,
  master: 999,
};

/**
 * STEP 4.3: ブラッシュアップ
 * 2カラムレイアウト、ブラッシュアップ履歴管理、バージョン比較
 */
const Step4_3_Refinement = ({
  articleUuid,
  articleData,
  generatedContent,
  onCompleted,
  onUpdateArticleData,
  isReadOnly = false,
  isGuest = false,
}) => {
  const navigate = useNavigate();
  const [executing, setExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [customPrompt, setCustomPrompt] = useState('');
  const [initialLoading, setInitialLoading] = useState(true); // 初期ローディング状態

  // ツアー管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
  } = useTutorialTour('generation_step4_3', { disabled: isGuest });

  // 親コンポーネントからのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => restartTour();
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () => window.removeEventListener('restartGenerationTour', handleRestartTour);
  }, [restartTour]);

  // ブラッシュアップ項目
  const [refinementItems, setRefinementItems] = useState(
    DEFAULT_REFINEMENT_ITEMS,
  );

  // ブラッシュアップ履歴（バージョン管理）
  const [refinementHistory, setRefinementHistory] = useState([
    {
      version: 0,
      timestamp: new Date().toISOString(),
      content: generatedContent || [],
      label: '初稿（生成直後）',
      is_original: true,
    },
  ]);
  const [currentVersion, setCurrentVersion] = useState(0);

  // 比較モーダル
  const [compareModalVisible, setCompareModalVisible] = useState(false);
  const [compareVersions, setCompareVersions] = useState({ from: 0, to: 0 });

  // 差分表示の切り替え
  const [showDiff, setShowDiff] = useState(true);

  // 新規生成されたコンテンツのID（フェードイン表示用）
  const [newContentIds, setNewContentIds] = useState(new Set());

  // ユーザープランと上限情報（APIから取得）
  const [userPlan, setUserPlan] = useState('free');
  const [userRole, setUserRole] = useState(null);
  const [creditCostPerRefinement, setCreditCostPerRefinement] = useState(10);

  // ブラッシュアップ回数の計算
  const refinementCount = useMemo(() => {
    return refinementHistory.filter(h => !h.is_original).length;
  }, [refinementHistory]);

  // ブラッシュアップ項目のトグル
  const handleToggleItem = itemId => {
    setRefinementItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item,
      ),
    );
  };

  // 初回ロード時に履歴を取得
  useEffect(() => {
    const loadHistory = async () => {
      if (!articleUuid) return;

      if (isGuest) {
        try {
          const contentData = await publicSamplesApi.getGeneratedContent(articleUuid);
          setRefinementHistory([{
            version: 0,
            timestamp: new Date().toISOString(),
            content: contentData.generated_contents || [],
            label: '初稿（生成直後）',
            is_original: true,
          }]);
          setCurrentVersion(0);
        } catch (err) {
          console.error('[Step4.3 Guest] Failed to load content:', err);
          setRefinementHistory([{
            version: 0,
            timestamp: new Date().toISOString(),
            content: generatedContent || [],
            label: '初稿（生成直後）',
            is_original: true,
          }]);
        }
        setInitialLoading(false);
        return;
      }

      try {
        // ローディング表示なし（バックグラウンドで読み込み）
        const history = await v2ArticlesApi.getRefinementHistory(articleUuid);
        // プランと上限情報を設定
        if (history.user_plan) setUserPlan(history.user_plan);
        if (history.user_role) setUserRole(history.user_role);
        if (history.credit_cost_per_refinement !== undefined)
          setCreditCostPerRefinement(history.credit_cost_per_refinement);

        if (history.history && history.history.length > 0) {
          const historyWithContent = await Promise.all(
            history.history.map(async h => {
              if (h.is_original || h.version === 0) {
                // 初稿もAPIから取得（Step4.2で生成されたデータ）
                try {
                  const contentData =
                    await v2ArticlesApi.getGeneratedContent(articleUuid);
                  return {
                    ...h,
                    content: contentData.generated_contents || [],
                  };
                } catch (err) {
                  console.error('[Step4.3] 初稿データ取得エラー:', err);
                  // エラーの場合はpropsから取得
                  return {
                    ...h,
                    content: generatedContent || [],
                  };
                }
              } else {
                // ブラッシュアップ後のバージョンはAPIから取得
                try {
                  const versionData = await v2ArticlesApi.getRefinementVersion(
                    articleUuid,
                    h.version,
                  );
                  return {
                    ...h,
                    content: versionData.sections || versionData.content || [],
                  };
                } catch (err) {
                  console.error(
                    `[Step4.3] バージョン${h.version}の取得エラー:`,
                    err,
                  );
                  return {
                    ...h,
                    content: [],
                  };
                }
              }
            }),
          );

          setRefinementHistory(historyWithContent);
          setCurrentVersion(history.current_version);
        } else {
          // 初稿のみの場合もAPIから取得
          try {
            const contentData =
              await v2ArticlesApi.getGeneratedContent(articleUuid);
            setRefinementHistory([
              {
                version: 0,
                timestamp: new Date().toISOString(),
                content: contentData.generated_contents || [],
                label: '初稿（生成直後）',
                is_original: true,
              },
            ]);
            setCurrentVersion(0);
          } catch (err) {
            console.error('[Step4.3] 初稿データ取得エラー（履歴なし）:', err);
            // エラーの場合はpropsから取得
            setRefinementHistory([
              {
                version: 0,
                timestamp: new Date().toISOString(),
                content: generatedContent || [],
                label: '初稿（生成直後）',
                is_original: true,
              },
            ]);
            setCurrentVersion(0);
          }
        }
      } catch (error) {
        console.error('[Step4.3] 履歴取得エラー:', error);
        // エラーの場合は初稿をAPIから取得
        try {
          const contentData =
            await v2ArticlesApi.getGeneratedContent(articleUuid);
          setRefinementHistory([
            {
              version: 0,
              timestamp: new Date().toISOString(),
              content: contentData.generated_contents || [],
              label: '初稿（生成直後）',
              is_original: true,
            },
          ]);
          setCurrentVersion(0);
        } catch (err) {
          console.error('[Step4.3] 初稿データ取得エラー（エラー時）:', err);
          // 最終的にpropsから取得
          setRefinementHistory([
            {
              version: 0,
              timestamp: new Date().toISOString(),
              content: generatedContent || [],
              label: '初稿（生成直後）',
              is_original: true,
            },
          ]);
          setCurrentVersion(0);
        }
      } finally {
        // データが揃ったらローディング終了
        setInitialLoading(false);
      }
    };

    if (articleUuid) {
      loadHistory();
    }
  }, [articleUuid, generatedContent]);

  // ツアーステップ定義
  const tourSteps = useMemo(() => {
    if (!TOUR_CONFIGS.generation_step4_3?.steps) return [];

    return TOUR_CONFIGS.generation_step4_3.steps.map(step => ({
      ...step,
      target: step.target ? step.target() : null,
    }));
  }, [tourOpen]);

  // ブラッシュアップを実行
  const handleExecuteRefinement = async () => {

    const selectedItems = refinementItems.filter(item => item.checked);
    if (selectedItems.length === 0) {
      message.warning('ブラッシュアップ項目を1つ以上選択してください');
      return;
    }

    setExecuting(true);
    setExecutionProgress(0);

    // 3分（180秒）のプログレスバー
    const progressInterval = setInterval(() => {
      setExecutionProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + (100 / 180) * 0.2; // 0.2秒ごとに更新
      });
    }, 200);

    try {
      await v2ArticlesApi.executeRefinement(articleUuid, {
        items: selectedItems.map(i => i.id),
        custom_prompt: customPrompt,
        current_version: currentVersion,
      });

      // ポーリングで結果を待つ
      let attempts = 0;
      const maxAttempts = 120;

      const pollResult = async () => {
        attempts++;

        const history = await v2ArticlesApi.getRefinementHistory(articleUuid);

        if (
          history.history &&
          history.history.length > refinementHistory.length
        ) {
          clearInterval(progressInterval);
          setExecutionProgress(100);

          message.success('ブラッシュアップが完了しました');

          // 履歴を更新（contentも取得）
          const historyWithContent = await Promise.all(
            history.history.map(async h => {
              if (h.is_original || h.version === 0) {
                // 初稿もAPIから取得（Step4.2で生成されたデータ）
                try {
                  const contentData =
                    await v2ArticlesApi.getGeneratedContent(articleUuid);
                  return {
                    ...h,
                    content: contentData.generated_contents || [],
                  };
                } catch (err) {
                  console.error(
                    '[Step4.3] ポーリング時の初稿データ取得エラー:',
                    err,
                  );
                  return {
                    ...h,
                    content: generatedContent || [],
                  };
                }
              } else {
                try {
                  const versionData = await v2ArticlesApi.getRefinementVersion(
                    articleUuid,
                    h.version,
                  );
                  return {
                    ...h,
                    content: versionData.sections || versionData.content || [],
                  };
                } catch (err) {
                  console.error(
                    `[Step4.3] バージョン${h.version}の取得エラー:`,
                    err,
                  );
                  return {
                    ...h,
                    content: [],
                  };
                }
              }
            }),
          );

          setRefinementHistory(historyWithContent);
          setCurrentVersion(history.current_version);

          // フェードイン表示のために新しく生成されたセクションIDを記録
          if (historyWithContent[history.current_version]?.content) {
            const newIds = new Set(
              historyWithContent[history.current_version].content.map(
                s => s.heading_id,
              ),
            );
            setNewContentIds(newIds);
            // 3秒後にクリア
            setTimeout(() => setNewContentIds(new Set()), 3000);
          }

          setExecuting(false);
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(pollResult, 5000);
        } else {
          clearInterval(progressInterval);
          throw new Error('タイムアウト');
        }
      };

      setTimeout(pollResult, 5000);
    } catch (error) {
      console.error('Failed to execute refinement:', error);
      clearInterval(progressInterval);
      // バックエンドからのエラーメッセージを表示（409: 他の記事のブラッシュアップ実行中など）
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'ブラッシュアップの実行に失敗しました';
      message.error(errorMessage);
      setExecuting(false);
      setExecutionProgress(0);
    }
  };

  // バージョンに戻す
  const handleRollback = version => {
    Modal.confirm({
      title: 'バージョンを戻しますか？',
      content: `「${refinementHistory[version].label}」の状態に戻ります。よろしいですか？`,
      icon: <RollbackOutlined />,
      okText: '戻す',
      cancelText: 'キャンセル',
      onOk: () => {
        setCurrentVersion(version);
        message.success(`バージョン${version}に戻しました`);
      },
    });
  };

  // 比較モーダルを開く
  const handleOpenCompare = (fromVersion, toVersion) => {
    setCompareVersions({ from: fromVersion, to: toVersion });
    setCompareModalVisible(true);
  };

  // 推敲をスキップして次へ
  const handleSkipToNext = () => {
    if (onCompleted) {
      const currentContent = refinementHistory[currentVersion].content;
      onCompleted(currentContent);
    }
  };

  // 確定して次へ
  const handleConfirmAndNext = async () => {
    // 確認モーダルを表示
    Modal.confirm({
      title: '原稿の確定',
      content: 'この原稿を確定してSTEP4.4（最終調整）に進みますか？',
      okText: '確定して次へ',
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          // ローディング表示
          message.loading({
            content: '原稿を確定しています...',
            key: 'confirmRefinement',
            duration: 0,
          });

          // 確定APIを呼び出し（ステータスが4.4に更新される）
          await v2ArticlesApi.confirmRefinementVersion(
            articleUuid,
            currentVersion,
          );

          // 記事データのcurrent_stepを更新（親コンポーネントに通知）
          if (onUpdateArticleData && articleData) {
            onUpdateArticleData({
              ...articleData,
              current_step: '4.4',
            });
          }

          // 成功メッセージ
          message.success({
            content: '原稿を確定しました',
            key: 'confirmRefinement',
            duration: 2,
          });

          // 遷移（articleDataが更新されたので、Step4Containerは4.4を表示する）
          navigate(`/aio/generation/${articleUuid}/step4-4`);
        } catch (error) {
          console.error('原稿確定エラー:', error);
          message.error({
            content: `原稿の確定に失敗しました: ${error.message}`,
            key: 'confirmRefinement',
            duration: 3,
          });
        }
      },
    });
  };

  // ブラッシュアップ実行中 - プログレスバーのみ表示（STEP2スタイル）
  const renderProgressBar = () => {
    if (!executing) return null;

    return (
      <div style={{ marginBottom: 24, padding: '24px 0' }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Spin />
            <Text strong style={{ marginLeft: 12, fontSize: 16 }}>
              記事をブラッシュアップしています...
            </Text>
          </div>
          <Progress
            percent={Math.floor(executionProgress)}
            status="active"
            strokeColor={{
              from: '#667eea',
              to: '#764ba2',
            }}
          />
          <Text
            type="secondary"
            style={{ fontSize: 12, textAlign: 'center', display: 'block' }}
          >
            処理には最大3分程度かかる場合があります
          </Text>
        </Space>
      </div>
    );
  };

  const currentContent = refinementHistory[currentVersion];
  const generatedSections = currentContent?.content || [];

  // Step4_2と同じロジックでタイトルを取得
  const workingDraft =
    articleData?.outline?.outline_data?.working_draft || null;
  const articleTitle =
    workingDraft?.title ||
    articleData?.title ||
    articleData?.keyword ||
    '無題の記事';

  // Markdownレンダリング関数
  const renderMarkdown = text => {
    if (!text) return null;
    let html = text;

    // 改行削除（Step4.2と同じ）: H2, H3, H4, UL, TABLE の前後の\nを削除
    html = html.replace(/\n+(\[H2\])/gi, '$1');
    html = html.replace(/(\[\/H2\])\n+/gi, '$1');
    html = html.replace(/\n+(\[H3\])/gi, '$1');
    html = html.replace(/(\[\/H3\])\n+/gi, '$1');
    html = html.replace(/\n+(\[H4\])/gi, '$1');
    html = html.replace(/(\[\/H4\])\n+/gi, '$1');
    html = html.replace(/\n+(\[UL\])/gi, '$1');
    html = html.replace(/(\[\/UL\])\n+/gi, '$1');
    html = html.replace(/\n+(\[TABLE\])/gi, '$1');
    html = html.replace(/(\[\/TABLE\])\n+/gi, '$1');

    // [H4]閉じタグ自動補完（Step4.2と同じ）
    html = html.replace(/\[H4\]([^\n]*?)(\n|$)/g, (match, content, lineEnd) => {
      if (content.includes('[/H4]')) {
        return match;
      }
      return `[H4]${content}[/H4]${lineEnd}`;
    });
    // [/H4]直後の改行も削除
    html = html.replace(/(\[\/H4\])\n/g, '$1');

    // [H2][H3][H4]タグをスタイル付きHTMLに変換（Step4.2と同じ）
    html = html.replace(
      /\[H2\](.*?)\[\/H2\]/g,
      '<h2 style="margin: 2em 0 1em; font-size: 20px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #fa8c16; color: white; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H2</span><span>$1</span></h2>',
    );
    html = html.replace(
      /\[H3\](.*?)\[\/H3\]/g,
      '<h3 style="margin: 2em 0 1em; font-size: 18px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #faad14; color: white; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H3</span><span>$1</span></h3>',
    );
    html = html.replace(
      /\[H4\](.*?)\[\/H4\]/g,
      '<h4 style="margin: 2em 0 1em; font-size: 16px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #f6ffed; border: 1px solid #b7eb8f; color: #389e0d; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H4</span><span>$1</span></h4>',
    );

    // Markdownフォーマット変換（独自情報のテキストエディタ用、Step4.2と同じ）
    // 画像: ![alt](url) → <img src="url" alt="alt" />
    html = html.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" style="max-width: 100%; height: auto; margin: 16px 0;" />',
    );
    // リンク: [text](url) → <a href="url">text</a>
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">$1</a>',
    );
    // 見出し: # text → <h2>text</h2> (複数の#にも対応)
    html = html.replace(
      /^#{1}\s+(.+)$/gm,
      '<h2 style="margin: 20px 0 12px; font-size: 20px; font-weight: 600;">$1</h2>',
    );
    html = html.replace(
      /^#{2}\s+(.+)$/gm,
      '<h3 style="margin: 16px 0 10px; font-size: 18px; font-weight: 600;">$1</h3>',
    );
    html = html.replace(
      /^#{3}\s+(.+)$/gm,
      '<h4 style="margin: 12px 0 8px; font-size: 16px; font-weight: 600;">$1</h4>',
    );
    // 箇条書き: - item → <ul><li>item</li></ul>
    html = html.replace(/(?:^- .+$\n?)+/gm, match => {
      const items = match
        .trim()
        .split('\n')
        .map(line => {
          const content = line.replace(/^-\s+/, '');
          return `<li>${content}</li>`;
        })
        .join('');
      return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${items}</ul>`;
    });

    html = html.replace(/\[UL\]([\s\S]*?)\[\/UL\]/g, (match, content) => {
      const items = content.match(/\[LI\](.*?)\[\/LI\]/g);
      if (!items) return match;
      const liTags = items
        .map(item => item.replace(/\[LI\](.*?)\[\/LI\]/, '<li>$1</li>'))
        .join('');
      return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${liTags}</ul>`;
    });
    html = html.replace(/\[TABLE\]([\s\S]*?)\[\/TABLE\]/g, (match, content) => {
      const rows = content.match(/\[TR\](.*?)\[\/TR\]/g);
      if (!rows) return match;
      const trTags = rows
        .map(row => {
          let cells = row.replace(/\[TR\]/, '').replace(/\[\/TR\]/, '');
          cells = cells.replace(
            /\[TH\](.*?)\[\/TH\]/g,
            '<th style="border: 1px solid #e8e8e8; padding: 12px; background: #fafafa; font-weight: 600;">$1</th>',
          );
          cells = cells.replace(
            /\[TD\](.*?)\[\/TD\]/g,
            '<td style="border: 1px solid #e8e8e8; padding: 12px;">$1</td>',
          );
          return `<tr>${cells}</tr>`;
        })
        .join('');
      return `<table style="width: 100%; border-collapse: collapse; margin: 2em 0 1em;">${trTags}</table>`;
    });
    html = html.replace(/\[B\](.*?)\[\/B\]/g, '<strong>$1</strong>');
    html = html.replace(/\n/g, '<br/>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(
      /\[(\d+)\]/g,
      '<sup style="color: #1890ff; font-weight: 600;">[$1]</sup>',
    );
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // 差分をハイライト表示するコンポーネント
  const renderWithDiff = (originalText, newText) => {
    if (!originalText || originalText === newText) {
      return renderMarkdown(newText);
    }

    // 1. まずマークアップタグをHTMLに変換（両方のテキストに対して）
    const convertMarkupToHtml = text => {
      let html = text;

      // 改行削除（Step4.2と同じ）: H2, H3, H4, UL, TABLE の前後の\nを削除
      html = html.replace(/\n+(\[H2\])/gi, '$1');
      html = html.replace(/(\[\/H2\])\n+/gi, '$1');
      html = html.replace(/\n+(\[H3\])/gi, '$1');
      html = html.replace(/(\[\/H3\])\n+/gi, '$1');
      html = html.replace(/\n+(\[H4\])/gi, '$1');
      html = html.replace(/(\[\/H4\])\n+/gi, '$1');
      html = html.replace(/\n+(\[UL\])/gi, '$1');
      html = html.replace(/(\[\/UL\])\n+/gi, '$1');
      html = html.replace(/\n+(\[TABLE\])/gi, '$1');
      html = html.replace(/(\[\/TABLE\])\n+/gi, '$1');

      // [H4]閉じタグ自動補完（Step4.2と同じ）
      html = html.replace(
        /\[H4\]([^\n]*?)(\n|$)/g,
        (match, content, lineEnd) => {
          if (content.includes('[/H4]')) {
            return match;
          }
          return `[H4]${content}[/H4]${lineEnd}`;
        },
      );
      // [/H4]直後の改行も削除
      html = html.replace(/(\[\/H4\])\n/g, '$1');

      // [H2][H3][H4]タグをスタイル付きHTMLに変換（Step4.2と同じ）
      html = html.replace(
        /\[H2\](.*?)\[\/H2\]/g,
        '<h2 style="margin: 2em 0 1em; font-size: 20px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #fa8c16; color: white; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H2</span><span>$1</span></h2>',
      );
      html = html.replace(
        /\[H3\](.*?)\[\/H3\]/g,
        '<h3 style="margin: 2em 0 1em; font-size: 18px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #faad14; color: white; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H3</span><span>$1</span></h3>',
      );
      html = html.replace(
        /\[H4\](.*?)\[\/H4\]/g,
        '<h4 style="margin: 2em 0 1em; font-size: 16px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #f6ffed; border: 1px solid #b7eb8f; color: #389e0d; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H4</span><span>$1</span></h4>',
      );

      // Markdownフォーマット変換（独自情報のテキストエディタ用、Step4.2と同じ）
      // 画像: ![alt](url) → <img src="url" alt="alt" />
      html = html.replace(
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        '<img src="$2" alt="$1" style="max-width: 100%; height: auto; margin: 16px 0;" />',
      );
      // リンク: [text](url) → <a href="url">text</a>
      html = html.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">$1</a>',
      );
      // 見出し: # text → <h2>text</h2> (複数の#にも対応)
      html = html.replace(
        /^#{1}\s+(.+)$/gm,
        '<h2 style="margin: 20px 0 12px; font-size: 20px; font-weight: 600;">$1</h2>',
      );
      html = html.replace(
        /^#{2}\s+(.+)$/gm,
        '<h3 style="margin: 16px 0 10px; font-size: 18px; font-weight: 600;">$1</h3>',
      );
      html = html.replace(
        /^#{3}\s+(.+)$/gm,
        '<h4 style="margin: 12px 0 8px; font-size: 16px; font-weight: 600;">$1</h4>',
      );
      // 箇条書き: - item → <ul><li>item</li></ul>
      html = html.replace(/(?:^- .+$\n?)+/gm, match => {
        const items = match
          .trim()
          .split('\n')
          .map(line => {
            const content = line.replace(/^-\s+/, '');
            return `<li>${content}</li>`;
          })
          .join('');
        return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${items}</ul>`;
      });

      // 箇条書き [UL][LI]...[/LI][/UL] → <ul><li>...</li></ul>
      html = html.replace(/\[UL\]([\s\S]*?)\[\/UL\]/g, (match, content) => {
        const items = content.match(/\[LI\](.*?)\[\/LI\]/g);
        if (!items) return match;
        const liTags = items
          .map(item => item.replace(/\[LI\](.*?)\[\/LI\]/, '<li>$1</li>'))
          .join('');
        return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${liTags}</ul>`;
      });

      // 表 [TABLE][TR][TH]...[/TH][TD]...[/TD][/TR][/TABLE] → <table>
      html = html.replace(
        /\[TABLE\]([\s\S]*?)\[\/TABLE\]/g,
        (match, content) => {
          const rows = content.match(/\[TR\](.*?)\[\/TR\]/g);
          if (!rows) return match;
          const trTags = rows
            .map(row => {
              let cells = row.replace(/\[TR\]/, '').replace(/\[\/TR\]/, '');
              cells = cells.replace(
                /\[TH\](.*?)\[\/TH\]/g,
                '<th style="border: 1px solid #e8e8e8; padding: 12px; background: #fafafa; font-weight: 600;">$1</th>',
              );
              cells = cells.replace(
                /\[TD\](.*?)\[\/TD\]/g,
                '<td style="border: 1px solid #e8e8e8; padding: 12px;">$1</td>',
              );
              return `<tr>${cells}</tr>`;
            })
            .join('');
          return `<table style="width: 100%; border-collapse: collapse; margin: 2em 0 1em;">${trTags}</table>`;
        },
      );

      // 太字 [B]...[/B] → <strong>...</strong>
      html = html.replace(/\[B\](.*?)\[\/B\]/g, '<strong>$1</strong>');

      // マークダウン太字（後方互換性のため残す） **text** → <strong>text</strong>
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // 引用番号 [n] をクリッカブルな<sup>に変換
      html = html.replace(
        /\[(\d+)\]/g,
        '<sup style="color: #1890ff; font-weight: 600;">[$1]</sup>',
      );

      return html;
    };

    // マークアップ変換後のテキストでdiffを取る
    const originalHtml = convertMarkupToHtml(originalText);
    const newHtml = convertMarkupToHtml(newText);

    // 2. 変換後のHTMLでdiffを取る
    const diff = Diff.diffWords(originalHtml, newHtml);
    let html = '';

    diff.forEach(part => {
      if (part.added) {
        html += `<span style="background-color: #e6f7ed; color: #0d7a3f; padding: 2px 4px; border-radius: 2px;">${part.value}</span>`;
      } else if (part.removed) {
        html += `<span style="background-color: #fce8e8; color: #c53030; text-decoration: line-through; padding: 2px 4px; border-radius: 2px;">${part.value}</span>`;
      } else {
        html += part.value;
      }
    });

    // 3. 改行を<br/>に変換
    html = html.replace(/\n/g, '<br/>');

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // メインUI（6:4 2カラムレイアウト）
  return (
    <div className="refinement-container">
      {/* 初期ローディング */}
      {initialLoading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#999' }}>
            ブラッシュアップデータを読み込んでいます...
          </div>
        </div>
      )}

      {/* プログレスバー（実行中のみ表示） */}
      {!initialLoading && renderProgressBar()}

      {/* 実行中は非表示 */}
      {!initialLoading && !executing && (
        <Row gutter={24} className="step4-3-layout">
          {/* 左カラム：本文表示 */}
          <Col span={15}>
            <Card
              title={
                <Space>
                  <Text strong>本文</Text>
                  <Badge
                    count={`バージョン${currentVersion}`}
                    style={{ backgroundColor: '#722ed1' }}
                  />
                </Space>
              }
              bordered={false}
              extra={
                <Space>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {(() => {
                      const total = generatedSections.reduce((sum, s) => {
                        const text = (s.content || '').replace(/<[^>]*>/g, '').replace(/\s/g, '');
                        return sum + text.length;
                      }, 0);
                      return `${total.toLocaleString()} 文字`;
                    })()}
                  </Text>
                  {refinementHistory.length > 1 && (
                    <>
                      <Checkbox
                        checked={showDiff}
                        onChange={e => setShowDiff(e.target.checked)}
                        disabled={isReadOnly}
                      >
                        差分を表示
                      </Checkbox>
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleOpenCompare(0, currentVersion)}
                        disabled={isReadOnly}
                      >
                        初稿と比較
                      </Button>
                    </>
                  )}
                </Space>
              }
            >
              <div
                style={{
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  padding: '8px 0',
                }}
              >
                {/* 記事タイトル */}
                <div style={{ marginBottom: 32 }}>
                  <Title level={2} style={{ marginBottom: 8 }}>
                    {articleTitle}
                  </Title>
                </div>

                {generatedSections.length === 0 ? (
                  <Empty
                    description="本文データが見つかりません"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  generatedSections.map((section, index) => {
                    // 初稿のセクションを取得（比較用）
                    const originalVersion = refinementHistory.find(
                      h => h.version === 0,
                    );
                    const originalSection = originalVersion?.content?.find(
                      s => s.heading_id === section.heading_id,
                    );
                    const hasChanges =
                      currentVersion > 0 &&
                      originalSection &&
                      originalSection.content !== section.content;

                    // リード文
                    if (section.heading_id === '__intro__') {
                      return (
                        <div
                          key={section.heading_id}
                          style={{ marginBottom: 48 }}
                        >
                          <div style={{ fontSize: 15, lineHeight: 1.8 }}>
                            {showDiff && hasChanges
                              ? renderWithDiff(
                                  originalSection.content,
                                  section.content,
                                )
                              : renderMarkdown(section.content)}
                          </div>
                          <Divider style={{ margin: '24px 0' }} />
                        </div>
                      );
                    }

                    // 各見出しの本文
                    const level = (section.heading_level || 'h2').toLowerCase();
                    const headingText =
                      section.heading_text || section.heading_title;
                    const isNew = newContentIds.has(section.heading_id);

                    return (
                      <div
                        key={section.heading_id || index}
                        className={
                          isNew
                            ? 'content-section-new'
                            : 'content-section-fadeIn'
                        }
                        style={{ marginBottom: 48 }}
                      >
                        {/* 見出し */}
                        <div style={{ marginBottom: 16 }}>
                          <Space size={8} align="center">
                            <Tag
                              color={
                                level === 'h2'
                                  ? 'orange'
                                  : level === 'h3'
                                    ? 'yellow'
                                    : 'green'
                              }
                              style={{ borderRadius: 999 }}
                            >
                              {level.toUpperCase()}
                            </Tag>
                            <Title
                              level={
                                level === 'h2' ? 3 : level === 'h3' ? 4 : 5
                              }
                              style={{ margin: 0 }}
                            >
                              {headingText}
                            </Title>
                            {hasChanges && (
                              <Tag color="orange" style={{ marginLeft: 8 }}>
                                変更あり
                              </Tag>
                            )}
                          </Space>
                        </div>

                        {/* 本文 */}
                        <div
                          style={{
                            lineHeight: 1.8,
                            fontSize: 15,
                            color: '#333',
                          }}
                        >
                          {showDiff && hasChanges
                            ? renderWithDiff(
                                originalSection.content,
                                section.content,
                              )
                            : renderMarkdown(section.content)}
                        </div>

                        {/* 文字数表示 */}
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {section.word_count || 0} 文字
                          </Text>
                        </div>

                        <Divider style={{ margin: '24px 0' }} />
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </Col>

          {/* 右カラム：ブラッシュアップ設定＆履歴 */}
          <Col span={9}>
            {/* ブラッシュアップ履歴 */}
            <Card
              title={
                <Space>
                  <HistoryOutlined />
                  <Text strong>ブラッシュアップ履歴</Text>
                </Space>
              }
              bordered={false}
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text type="secondary">実行回数</Text>
                  <Text strong>
                    {refinementCount} 回
                  </Text>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text type="secondary">消費クレジット</Text>
                  <Text strong style={{ color: '#1677ff' }}>
                    {userRole === 'admin' || userRole === 'master'
                      ? '無料'
                      : `${creditCostPerRefinement}cr / 回`}
                  </Text>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <Timeline
                  items={refinementHistory.map(h => ({
                    color: h.version === currentVersion ? 'blue' : 'gray',
                    children: (
                      <div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text
                            strong={h.version === currentVersion}
                            style={{ fontSize: 13 }}
                          >
                            {h.label}
                          </Text>
                          {h.version !== currentVersion && (
                            <Space size={4}>
                              <Tooltip title="このバージョンと比較">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={() =>
                                    handleOpenCompare(h.version, currentVersion)
                                  }
                                  disabled={isReadOnly}
                                />
                              </Tooltip>
                              <Tooltip title="このバージョンに戻す">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<RollbackOutlined />}
                                  onClick={() => handleRollback(h.version)}
                                  disabled={isReadOnly}
                                />
                              </Tooltip>
                            </Space>
                          )}
                        </div>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 11,
                            display: 'block',
                            marginTop: 4,
                          }}
                        >
                          {new Date(h.timestamp).toLocaleString('ja-JP', {
                            timeZone: 'Asia/Tokyo',
                          })}
                        </Text>
                      </div>
                    ),
                  }))}
                />
              </Space>
            </Card>

            {/* ブラッシュアップ項目 */}
            <Card
              title={<Text strong>ブラッシュアップ項目</Text>}
              bordered={false}
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {refinementItems.map(item => (
                  <Checkbox
                    key={item.id}
                    checked={item.checked}
                    onChange={() => handleToggleItem(item.id)}
                    disabled={isReadOnly}
                  >
                    {item.label}
                  </Checkbox>
                ))}
              </Space>
            </Card>

            {/* カスタム指示 */}
            <Card title={<Text strong>追加指示（任意）</Text>} bordered={false}>
              <TextArea
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
                placeholder="例: 専門用語を減らして初心者向けに..."
                rows={4}
                maxLength={1000}
                showCount
                disabled={isReadOnly}
                style={{
                  background: '#fafafa',
                  border: 'none',
                }}
              />
              {/* ブラッシュアップ実行ボタン - 追加指示の下に配置 */}
              {!executing && (
                <Button
                  size="large"
                  icon={<ThunderboltOutlined />}
                  onClick={handleExecuteRefinement}
                  disabled={isReadOnly || executing}
                  loading={executing}
                  block
                  style={{ marginTop: 36 }}
                >
                  ブラッシュアップを実行{userRole !== 'admin' && userRole !== 'master' ? ` (${creditCostPerRefinement}cr)` : ''}
                </Button>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* 最下部：原稿確定ボタン（全幅） */}
      {!executing && (
        <div
          style={{
            marginTop: 48,
            padding: '24px 0',
            borderTop: '1px solid #e8e8e8',
          }}
        >
          <Button
            type="primary"
            size="large"
            icon={<CheckOutlined />}
            onClick={handleConfirmAndNext}
            disabled={isReadOnly || executing}
            block
          >
            原稿を確定して次へ
          </Button>
        </div>
      )}

      {/* 比較モーダル */}
      <Modal
        title="バージョン比較"
        open={compareModalVisible}
        onCancel={() => setCompareModalVisible(false)}
        width={1400}
        footer={[
          <Button key="close" onClick={() => setCompareModalVisible(false)}>
            閉じる
          </Button>,
        ]}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Card
              title={`${refinementHistory[compareVersions.from]?.label || `v${compareVersions.from}`}`}
              size="small"
              bordered={false}
              style={{
                background: '#fffcf5',
                maxHeight: 600,
                overflowY: 'auto',
              }}
            >
              {refinementHistory[compareVersions.from]?.content?.map(
                (section, index) => {
                  if (section.heading_id === '__intro__') {
                    return (
                      <div
                        key={section.heading_id}
                        style={{ marginBottom: 16 }}
                      >
                        <Text
                          strong
                          style={{
                            display: 'block',
                            marginBottom: 8,
                            fontSize: 14,
                          }}
                        >
                          リード文
                        </Text>
                        <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                          {renderMarkdown(section.content)}
                        </div>
                        <Divider style={{ margin: '12px 0' }} />
                      </div>
                    );
                  }

                  return (
                    <div
                      key={section.heading_id || index}
                      style={{ marginBottom: 16 }}
                    >
                      <div style={{ marginBottom: 6 }}>
                        <Tag color="orange" style={{ fontSize: 11 }}>
                          {(section.heading_level || 'h2').toUpperCase()}
                        </Tag>
                        <Text strong style={{ fontSize: 14 }}>
                          {section.heading_text || section.heading_title}
                        </Text>
                      </div>
                      <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                        {renderMarkdown(section.content)}
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                    </div>
                  );
                },
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card
              title={`${refinementHistory[compareVersions.to]?.label || `v${compareVersions.to}`} (差分表示)`}
              size="small"
              bordered={false}
              style={{
                background: '#f5fbff',
                maxHeight: 600,
                overflowY: 'auto',
              }}
            >
              {refinementHistory[compareVersions.to]?.content?.map(
                (section, index) => {
                  const originalSection = refinementHistory[
                    compareVersions.from
                  ]?.content?.find(s => s.heading_id === section.heading_id);

                  if (section.heading_id === '__intro__') {
                    return (
                      <div
                        key={section.heading_id}
                        style={{ marginBottom: 16 }}
                      >
                        <Text
                          strong
                          style={{
                            display: 'block',
                            marginBottom: 8,
                            fontSize: 14,
                          }}
                        >
                          リード文
                        </Text>
                        <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                          {originalSection
                            ? renderWithDiff(
                                originalSection.content,
                                section.content,
                              )
                            : renderMarkdown(section.content)}
                        </div>
                        <Divider style={{ margin: '12px 0' }} />
                      </div>
                    );
                  }

                  return (
                    <div
                      key={section.heading_id || index}
                      style={{ marginBottom: 16 }}
                    >
                      <div style={{ marginBottom: 6 }}>
                        <Tag
                          color={
                            (section.heading_level || 'h2').toLowerCase() ===
                            'h2'
                              ? 'orange'
                              : (
                                    section.heading_level || 'h2'
                                  ).toLowerCase() === 'h3'
                                ? 'yellow'
                                : 'green'
                          }
                          style={{ fontSize: 11 }}
                        >
                          {(section.heading_level || 'h2').toUpperCase()}
                        </Tag>
                        <Text strong style={{ fontSize: 14 }}>
                          {section.heading_text || section.heading_title}
                        </Text>
                      </div>
                      <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                        {originalSection
                          ? renderWithDiff(
                              originalSection.content,
                              section.content,
                            )
                          : renderMarkdown(section.content)}
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                    </div>
                  );
                },
              )}
            </Card>
          </Col>
        </Row>
      </Modal>

      {/* プロダクトツアー */}
      <EnhancedTour
        open={tourOpen}
        steps={tourSteps}
        onClose={completeTour}
        onFinish={completeTour}
        onSkip={() => skipTour()}
        current={currentStep}
        onChange={updateCurrentStep}
        tourTitle="STEP4.3: ブラッシュアップ"
      />
    </div>
  );
};

export default Step4_3_Refinement;
